import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  inject,
  input,
  signal,
} from "@angular/core";
import { Router } from "@angular/router";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { SharedModule } from "../../../../shared/shared.module";
import { PercentComponent } from "../../../../shared/components/percent/percent.component";
import { TypePercent } from "../../../../shared/enums/enums/typePercent.enum";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { MatMenuModule } from "@angular/material/menu";
import { MatIconModule } from "@angular/material/icon";
import { AideContextComponent } from "../../../../shared/components/aide-context/aide-context.component";
import { AideContextSection } from "../../../../shared/models/aide-context.model";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";
import { LignesFacadeService } from "../../../ligne/services/lignes/lignes-facade.service";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { LoaderSkeletonComponent } from "../../../../shared/loader-skeleton/loader-skeleton.component";
import { OF } from "../../../of/models/of.model";

type SortBy = "DATE" | "QTE" | "SCORE";
type SortOrder = "DESC" | "ASC";

@Component({
  selector: "vl-widget-ofs-ligne",
  standalone: true,
  imports: [
    CommonModule,
    WidgetTitleComponent,
    SharedModule,
    PercentComponent,
    ChipsToggleComponent,
    ChipsComponent,
    MatMenuModule,
    MatIconModule,
    AideContextComponent,
    LoaderSkeletonComponent,
  ],
  templateUrl: "./widget-ofs-ligne.component.html",
  styleUrls: ["./widget-ofs-ligne.component.scss"],
  host: { class: "widget-component" },
})
export class WidgetOfsLigneComponent {
  widget = input<Widget>();
  metadata = input<EntityMetadata>();

  private ligneFacade = inject(LignesFacadeService);
  private articleInfosFacade = inject(ArticlesInfosFacadeService);
  private router = inject(Router);
  stats = this.ligneFacade.getState().stats;

  aideSections: AideContextSection[] = [
    { type: "text", html: TranslateUtils.translate("stats_ligne.aide_ofs") },
    { type: "highlight", html: TranslateUtils.translate("stats_ligne.aide_ofs_formule") },
  ];

  sortBy = signal<SortBy>("DATE");
  sortOrder = signal<SortOrder>("DESC");
  search = signal<string>("");

  elementsSort = [
    { name: TranslateUtils.translate("stats_ligne.tri_date"), value: "DATE" },
    { name: TranslateUtils.translate("stats_ligne.tri_quantite"), value: "QTE" },
    { name: TranslateUtils.translate("stats_ligne.tri_score"), value: "SCORE" },
  ];
  elementsOrder = [
    { name: TranslateUtils.translate("stats_ligne.tri_desc"), value: "DESC" },
    { name: TranslateUtils.translate("stats_ligne.tri_asc"), value: "ASC" },
  ];

  rows = computed(() => {
    const ofs: OF[] = this.stats()?.ofs ?? [];
    const q = this.search().trim().toLowerCase();
    const by = this.sortBy();
    const order = this.sortOrder();

    let filtered = ofs.map((of) => ({
      id: of.id,
      numero: of.numero ?? "",
      articleDenom:
        this.articleInfosFacade.getByArticleId(of.articleId)?.()?.denomination ?? "",
      quantiteProduite: parseNumber(of.quantiteProduite ?? 0),
      dateDebut: of.dateDebut,
      dateFin: of.dateFin,
      scoreTotal: Math.round(parseNumber(of.scoreTotal ?? 0) * 100) / 100,
      etat: of.etat,
    }));

    if (q) {
      filtered = filtered.filter(
        (r) =>
          r.numero.toLowerCase().includes(q) ||
          r.articleDenom.toLowerCase().includes(q),
      );
    }

    filtered.sort((a, b) => {
      let va = 0,
        vb = 0;
      switch (by) {
        case "QTE":
          va = a.quantiteProduite;
          vb = b.quantiteProduite;
          break;
        case "SCORE":
          va = a.scoreTotal;
          vb = b.scoreTotal;
          break;
        case "DATE":
        default:
          va = new Date(a.dateFin ?? 0).getTime();
          vb = new Date(b.dateFin ?? 0).getTime();
          break;
      }
      return (vb - va) * (order === "DESC" ? 1 : -1);
    });

    return filtered;
  });

  onGoOF(ofId: string) {
    this.router.navigate([`/productions/suivi/stats`], {
      queryParams: { ofId },
    });
  }

  protected readonly TypePercent = TypePercent;
}
